import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { contactAPI } from '../services/api';
import { toast } from 'sonner';

export const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await contactAPI.submit(formData);
      if (result.success) {
        toast.success('Nachricht erfolgreich gesendet!');
        setFormData({ name: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      toast.error(error.message || 'Nachricht konnte nicht gesendet werden');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-black to-[#0a0a0a] relative">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.contact.title}
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="bg-[#1a1a1a] border-[#C4A55A]/20">
            <CardHeader>
              <CardTitle className="text-2xl text-white">Nachricht senden</CardTitle>
              <CardDescription className="text-gray-400">
                Füllen Sie das Formular aus und wir melden uns bei Ihnen
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-300">{t.contact.form.name}</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A] transition-colors duration-300"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">{t.contact.form.email}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A] transition-colors duration-300"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-gray-300">{t.contact.form.subject}</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A] transition-colors duration-300"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-300">{t.contact.form.message}</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A] transition-colors duration-300 resize-none"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold transition-all duration-300 hover:scale-105 disabled:opacity-50"
                >
                  {loading ? 'Wird gesendet...' : t.contact.form.submit}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-8">
            <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-[#C4A55A]/20 hover:border-[#C4A55A]/40 transition-all duration-300 group">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C4A55A]/20 flex items-center justify-center group-hover:bg-[#C4A55A]/30 transition-colors duration-300">
                    <MapPin className="h-6 w-6 text-[#C4A55A]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{t.contact.info.address}</h3>
                    <p className="text-gray-400">{t.contact.info.addressValue}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-[#C4A55A]/20 hover:border-[#C4A55A]/40 transition-all duration-300 group">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C4A55A]/20 flex items-center justify-center group-hover:bg-[#C4A55A]/30 transition-colors duration-300">
                    <Phone className="h-6 w-6 text-[#C4A55A]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{t.contact.info.phone}</h3>
                    <p className="text-gray-400">{t.contact.info.phoneValue}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-[#C4A55A]/20 hover:border-[#C4A55A]/40 transition-all duration-300 group">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C4A55A]/20 flex items-center justify-center group-hover:bg-[#C4A55A]/30 transition-colors duration-300">
                    <Mail className="h-6 w-6 text-[#C4A55A]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{t.contact.info.email}</h3>
                    <p className="text-gray-400">{t.contact.info.emailValue}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Map */}
            <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-[#C4A55A]/20 overflow-hidden" data-testid="contact-map-card">
              <div className="relative h-64">
                <iframe
                  title="Löwenherz Invest AG - Standort Karte"
                  data-testid="contact-map-iframe"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=8.617%2C47.679%2C8.625%2C47.685&layer=mapnik&marker=47.6824%2C8.6210"
                  className="absolute inset-0 w-full h-full grayscale-[0.4] contrast-110"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  style={{ border: 0 }}
                />
              </div>
              <div className="p-4 bg-black/40 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-2">
                  <MapPin className="h-5 w-5 text-[#C4A55A] shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-white font-semibold">Löwenherz Invest AG</p>
                    <p className="text-gray-400">Wildenstrasse 3, 8212 Neuhausen am Rheinfall</p>
                  </div>
                </div>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=Wildenstrasse+3,+8212+Neuhausen+am+Rheinfall,+Schweiz"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-map-directions-btn"
                  className="text-xs font-medium px-3 py-1.5 rounded-md bg-[#C4A55A]/15 border border-[#C4A55A]/40 text-[#C4A55A] hover:bg-[#C4A55A]/25 transition-colors"
                >
                  Route planen →
                </a>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};